import { useDroppable } from '@dnd-kit/core';
import { ChevronRight, Folder, Lock, MoreVertical } from 'lucide-react';
import type { JobFolder } from '@jobos/shared';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFileDrop } from '@/hooks/useFileDrop';
import { useJobFolders } from '@/hooks/useJobFolders';
import { folderDropId } from './jobFileDrag';

// The folder row that sits above the file grid. It only draws folders and the
// breadcrumb — the files themselves stay with the grid and list components,
// which already know how to render them.
//
// Folders nest, so this draws whichever level is open: the folders whose parent
// is the open folder, and a breadcrumb of up to three segments back to the job
// root. The API hands back every folder on the job as one flat list, which is
// why finding a level is a filter here rather than a fetch.
//
// The folder area does not change with the view toggle at all. That toggle is
// about how the *files* are presented; a folder is the same object either way,
// and Drive leaves its folder chips in place when the files below switch to a
// table. Reshaping them too made the toggle feel like it changed more than it
// did.
//
// The chip is deliberately not shaped like a file card. A card is tall and led
// by its preview, because what a photo *is* is the thing worth showing; a
// folder has no preview and its name is the whole point, so it gets a short
// wide row with the name at full width. Making them the same shape made
// folders read as files with a broken thumbnail.
//
// Mobile-first at 390px. A tile is comfortably past any tap-target guideline;
// the rows keep an explicit 44px floor, because the chip row this replaced
// measured 26px in QA.

interface JobFolderNavigatorProps {
  jobId: string;
  /** The open folder, or null for the job root. */
  folderId: string | null;
  onNavigate: (folderId: string | null) => void;
  /** How many files the open folder holds, so an empty one can say so. */
  fileCount: number;
  onRename?: (folder: JobFolder) => void;
  onDelete?: (folder: JobFolder) => void;
  /**
   * Files dragged in from the desktop onto a folder tile. Uploading into a
   * folder without opening it first is the whole point, so the tile is a target
   * for an OS drag as well as for the app's own file→folder drags.
   */
  onFilesDropped?: (files: File[], folderId: string) => void;
}

export function JobFolderNavigator({
  jobId,
  folderId,
  onNavigate,
  fileCount,
  onRename,
  onDelete,
  onFilesDropped,
}: JobFolderNavigatorProps) {
  const { data: folders, isLoading } = useJobFolders(jobId);

  const open = folders?.find((candidate) => candidate.id === folderId) ?? null;
  const parent = open?.parentFolderId
    ? (folders?.find((candidate) => candidate.id === open.parentFolderId) ?? null)
    : null;

  // The folders one level below wherever we are. The API returns the job's
  // folders as a flat list precisely so this is a filter rather than a fetch.
  const children = (folders ?? []).filter(
    (candidate) => (candidate.parentFolderId ?? null) === folderId,
  );

  // Nothing rather than an empty folder list while the query is in flight: a
  // list that appears empty and then fills reads as files having been lost.
  if (isLoading || !folders) return null;
  if (folderId === null && children.length === 0) return null;

  return (
    <div className="space-y-3">
      {folderId && (
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm">
          <BreadcrumbDropTarget folderId={null} testId="job-folder-drop-root">
            <button
              type="button"
              onClick={() => onNavigate(null)}
              className="rounded px-1 py-1 font-medium text-blue-600 hover:underline"
            >
              All files
            </button>
          </BreadcrumbDropTarget>
          {parent && (
            <>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
              {/* min-w-0 truncate, not just truncate: at 390px a long parent name
                  would otherwise push the open folder's own name off-screen. */}
              <BreadcrumbDropTarget folderId={parent.id} testId="job-folder-drop-parent">
                <button
                  type="button"
                  onClick={() => onNavigate(parent.id)}
                  className="min-w-0 truncate rounded px-1 py-1 font-medium text-blue-600 hover:underline"
                >
                  {parent.name}
                </button>
              </BreadcrumbDropTarget>
            </>
          )}
          <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
          <span className="truncate text-gray-700">{open?.name ?? 'Folder'}</span>
        </nav>
      )}

      {children.length > 0 && (
        <ul
          aria-label="Folders"
          // Fewer, wider tracks than JobFilesGrid: a chip needs room for a name on
          // one line, where a card only needs room for a square.
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
        >
          {children.map((child) => (
            <FolderTile
              key={child.id}
              folder={child}
              onNavigate={onNavigate}
              {...(onRename ? { onRename } : {})}
              {...(onDelete ? { onDelete } : {})}
              {...(onFilesDropped ? { onFilesDropped } : {})}
            />
          ))}
        </ul>
      )}

      {folderId !== null && fileCount === 0 && children.length === 0 && (
        <p className="rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          This folder is empty.
        </p>
      )}
    </div>
  );
}

/**
 * A breadcrumb segment that accepts dropped files.
 *
 * The breadcrumb is the only way a file gets back *out* of a folder by drag —
 * there is no tile for "up".
 */
function BreadcrumbDropTarget({
  folderId,
  testId,
  children,
}: {
  folderId: string | null;
  testId: string;
  children: React.ReactNode;
}) {
  const dropId = folderDropId(folderId);
  const { setNodeRef, isOver } = useDroppable({ id: dropId });

  return (
    <span
      ref={setNodeRef}
      data-testid={testId}
      data-drop-id={dropId}
      className={`min-w-0 rounded ${isOver ? 'bg-blue-50 ring-2 ring-blue-500' : ''}`}
    >
      {children}
    </span>
  );
}

interface FolderProps {
  folder: JobFolder;
  onNavigate: (folderId: string) => void;
  onRename?: (folder: JobFolder) => void;
  onDelete?: (folder: JobFolder) => void;
  onFilesDropped?: (files: File[], folderId: string) => void;
}

/** Shared wording for the one folder the app owns. */
const SYSTEM_TITLE = 'Built-in folder';

/**
 * What a tile says beneath its name.
 *
 * A folder holding only subfolders would otherwise read "0 files", which is
 * true of its own contents and misleading about the folder.
 */
function describeContents(folder: JobFolder): string {
  const parts: string[] = [];
  if (folder.subfolderCount > 0) {
    parts.push(`${folder.subfolderCount} ${folder.subfolderCount === 1 ? 'folder' : 'folders'}`);
  }
  if (folder.fileCount > 0 || parts.length === 0) {
    parts.push(`${folder.fileCount} ${folder.fileCount === 1 ? 'file' : 'files'}`);
  }
  return parts.join(' · ');
}

function FolderTile({ folder, onNavigate, onRename, onDelete, onFilesDropped }: FolderProps) {
  const isSystem = folder.slug !== null;
  const dropId = folderDropId(folder.id);
  const { setNodeRef, isOver } = useDroppable({ id: dropId });

  // The app's own file→folder drag is dnd-kit, which runs on pointer events; a
  // drag off the desktop is HTML5 and fires none of those. So a tile carries
  // both, and only one of them can ever be in flight — `isOver` is the app's
  // drag, `isFileOver` the OS's, and they light the tile the same way because
  // to the person dragging they are the same gesture.
  const { isDragOver: isFileOver, dropHandlers } = useFileDrop({
    onFiles: (files) => onFilesDropped?.(files, folder.id),
    disabled: !onFilesDropped,
  });

  return (
    <li
      ref={setNodeRef}
      {...(onFilesDropped ? dropHandlers : {})}
      data-drop-id={dropId}
      aria-label={folder.name}
      // The same blue ring the breakdown drop zones use, so a drop target looks
      // the same wherever it turns up in the app.
      className={`flex min-h-[72px] items-center gap-1 rounded-xl pr-1.5 transition-colors ${
        isOver || isFileOver
          ? 'bg-blue-50 ring-2 ring-blue-500'
          : 'bg-gray-100 hover:bg-gray-200/70'
      }`}
    >
      {/* The chip itself is the tap target. Labelled explicitly: the name lives
          in a span the button also owns, but the icon carries no text, and an
          aria-label keeps the announced name free of the file count. */}
      <button
        type="button"
        onClick={() => onNavigate(folder.id)}
        aria-label={folder.name}
        title={isSystem ? SYSTEM_TITLE : undefined}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-3 py-3 text-left"
      >
        {/* A filled badge rather than a bare outline: it reads as an object at a
            glance, where a thin glyph on a grey field disappears. */}
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-600 text-white"
          aria-hidden="true"
        >
          <Folder className="h-5 w-5" fill="currentColor" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-gray-900" title={folder.name}>
            {folder.name}
          </span>
          <span className="block text-xs text-gray-500">{describeContents(folder)}</span>
        </span>
      </button>

      {isSystem ? (
        <Lock className="mr-1.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
      ) : (
        <FolderActionsMenu
          folder={folder}
          {...(onRename ? { onRename } : {})}
          {...(onDelete ? { onDelete } : {})}
        />
      )}
    </li>
  );
}

/**
 * Rename and delete behind a menu, as the file card does.
 *
 * A chip has no room for two icon buttons beside the name without crowding it
 * out, and the grid already trains the eye to look for the ⋮.
 */
function FolderActionsMenu({ folder, onRename, onDelete }: Omit<FolderProps, 'onNavigate'>) {
  if (!onRename && !onDelete) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Actions for ${folder.name}`}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-300/60"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onRename && (
          <DropdownMenuItem onClick={() => onRename(folder)}>Rename</DropdownMenuItem>
        )}
        {onDelete && (
          <DropdownMenuItem
            onClick={() => onDelete(folder)}
            className="text-red-600 hover:bg-red-50 focus:bg-red-50"
          >
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
